import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import hasAuthToken from 'lib/hasAuthToken';
import React, { useRef } from 'react';
import Layout from 'components/Layout';
import Breadcrumbs from 'components/Breadcrumbs';
import { capitalizeFirstLetter } from 'utils';
import useBrowsePerPlace from 'utils/useBrowsePerPlace';
import Pagination from 'components/Pagination';
import JobPostingList from 'components/JobPostingList';
import mapElasticJobPostings from 'lib/mapElasticJobPostings';

function PlacePage({
  query,
  href,
  items: initItems,
  totalResults: initTotalResults,
}) {
  const { t } = useTranslation();
  const place = capitalizeFirstLetter(query.place);
  const scrollToRef = useRef(null);
  const {
    items,
    totalResults,
    loading,
    currentPage,
    totalPages,
    handleNextPage,
    handlePrevPage,
  } = useBrowsePerPlace({
    place,
    initItems,
    initTotalResults,
    scrollToRef,
  });
  const headingKey = totalResults
    ? `vacanciesPerPlace.headingDefault`
    : 'vacanciesPerPlace.noResults.heading';
  const title = t('vacanciesPerPlace.pageTitle', { place });

  return (
    <Layout
      title={title}
      metaDescription={`Zoek vacatures in ${place}. Werken bij bedrijven in de regio ${place}. Bij Vacaturebank met vacatures van werkgevers in Nederland - deBanenSite.nl vind je een overzicht van vacatures in ${place}. Bekijk de baan en solliciteer direct!`}
    >
      <div className="o-retain o-retain--wall u-margin-top-huge u-margin-bottom-huge">
        <Breadcrumbs
          links={[
            {
              label: t('jobPostingOverview.pageTitle'),
              href: '/vacatures',
            },
            {
              label: title,
              href,
            },
          ]}
        />
        <h1 className="u-margin-bottom-base" ref={scrollToRef}>
          {t(headingKey, {
            place,
            count: totalResults || 0,
            context: loading ? '_loading' : '',
          })}
        </h1>
        <p className="u-text-retain-none">
          Vacatures beschikbaar in {place}. Op zoek naar een baan in {place}?
          Bij Vacaturebank met vacatures van werkgevers in Nederland -
          deBanenSite.nl vind je een overzicht van vacatures in {place}. Bekijk
          de banen waar je wil werken en solliciteer direct!
        </p>

        {!totalResults && (
          <p
            data-cy="no-results"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t('vacanciesPerPlace.noResults.text', {
                interpolation: { escapeValue: false },
              }),
            }}
          />
        )}

        {!!items?.length && (
          <JobPostingList
            jobPostings={mapElasticJobPostings(items)}
            loading={loading}
            currentPage={currentPage}
            cta={t('vacanciesPerPlace.viewJobPosting')}
            test="vacancies-list"
          />
        )}

        <div className="o-retain--lap">
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              loading={loading}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

export async function getServerSideProps({ req, res, query, locale }) {
  if (!hasAuthToken(req)) {
    res.setHeader(
      'Cache-Control',
      'public, max-age=300, s-maxage=300, stale-while-revalidate=600'
    );
  }

  const { place, page = 1 } = query;
  const { items, totalResults } = await fetch(
    `${process.env.NEXT_API_ROUTE}/browse/place?place=${place}&page=${page}`
  )
    .then(resp => resp.json())
    .then(data => ({
      items: data.results,
      totalResults: data.totalResults,
    }));

  return {
    props: {
      href: req.url,
      query,
      items,
      totalResults,
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
}

export default PlacePage;
